"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover as PopoverPrimitive } from "radix-ui";

export type MultiSelectOption = { value: string; label: string; color?: string };

type MultiSelectProps = {
  values: string[];
  onValuesChange: (values: string[]) => void;
  options: MultiSelectOption[];
  allLabel: string;
  selectedLabel: string;
  icon?: ReactNode;
  triggerClassName?: string;
};

export function MultiSelect({ values, onValuesChange, options, allLabel, selectedLabel, icon, triggerClassName }: MultiSelectProps) {
  const enabled = values.filter((value) => options.some((option) => option.value === value));
  const selectedNames = options.filter((option) => enabled.includes(option.value)).map((option) => option.label);
  const description = !enabled.length ? allLabel : enabled.length === 1 ? selectedNames[0] : `${enabled.length} ${selectedLabel}`;

  return <PopoverPrimitive.Root>
    <PopoverPrimitive.Trigger asChild>
      <button type="button" className={cn("flex h-10 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm outline-none hover:bg-accent/30 focus-visible:ring-2 focus-visible:ring-ring", triggerClassName)} aria-label={description}>
        {icon && <span className="shrink-0 text-muted-foreground [&_svg]:size-4">{icon}</span>}
        <span className="min-w-0 flex-1 truncate">{description}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content align="start" sideOffset={4} collisionPadding={12} className="z-[80] max-h-72 w-[var(--radix-popover-trigger-width)] min-w-[220px] overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg" onOpenAutoFocus={(event) => event.preventDefault()}>
        <button type="button" className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-ring" onClick={() => onValuesChange([])}>
          {!enabled.length ? <Check className="size-4" /> : <X className="size-4 opacity-60" />}
          {allLabel}
        </button>
        <div className="my-1 border-t" />
        {options.map((option) => {
          const checked = enabled.includes(option.value);
          return <button key={option.value} type="button" role="checkbox" aria-checked={checked} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-ring" onClick={() => onValuesChange(checked ? enabled.filter((value) => value !== option.value) : [...enabled, option.value])}>
            <span className="flex size-4 shrink-0 items-center justify-center rounded border border-input">{checked && <Check className="size-3 text-primary" />}</span>
            {option.color && <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: option.color }} />}
            <span className="min-w-0 break-words">{option.label}</span>
          </button>;
        })}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}
